import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { ChevronRightIconComponent } from '@itero/ui-toolkit-angular/icons';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxIntuitiveCrownComponent } from '@modules/rx-intuitive-crown/component/rx-intuitive-crown.component';
import { IntuitiveIndication } from '@modules/rx-intuitive-teeth-diagram/models/intuitive-indication';
import { IntuitiveToothForTreatment } from '@modules/rx-intuitive-teeth-diagram/models';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { CrownSelectionContext } from '@modules/rx-intuitive-crown/models/crown-models';
import { IdName } from '@shared/models/id-name';
import { MaterialOption } from '@shared/models/material-option';
import { PonticDesignEnum } from '@shared/models/pontic-design.enum';
import { RxIntuitiveCrownAdditionalInfoRulesService } from '@modules/rx-intuitive-crown-additional-info/services/rx-intuitive-crown-additional-info-rules.service';
import { RxIntuitiveAdditionalInfoModalService } from '@modules/rx-intuitive-crown-additional-info/components/rx-intuitive-additional-info-modal/rx-intuitive-additional-info-modal.service';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { extractAdditionalInfoIds } from '@modules/rx-intuitive-crown-additional-info/utils/extract-additional-info-ids';
import { getDisabledFieldsClearPayload } from '@modules/rx-intuitive-crown-additional-info/utils/get-disabled-fields-clear-payload';
import { RxIntuitiveIndicationRulesService } from '@modules/rx-intuitive-teeth-diagram/services/rx-intuitive-indication-rules.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { LoggerService } from '@core/services/logger/logger.service';
import { ToothEditorSharedModule } from '@modules/tooth-editor/tooth-editor-shared.module';
import { RxIntuitiveTeethDiagramBridgeFacade } from '@modules/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram-bridge.facade';
import {
	buildTreatmentPreviewLine,
	TREATMENT_PREVIEW_LINE_EMPTY
} from '@modules/rx-intuitive-teeth-diagram/utils/build-treatment-preview-line';

@Component({
	selector: 'rx-intuitive-tooth-editor-body',
	templateUrl: './rx-intuitive-tooth-editor-body.component.html',
	styleUrls: ['./rx-intuitive-tooth-editor-body.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		TlkButtonComponent,
		TlkCardComponent,
		RxIntuitiveCrownComponent,
		ReactiveFormsModule,
		ToothEditorSharedModule,
		ChevronRightIconComponent
	],
	providers: [RxIntuitiveTeethDiagramBridgeFacade]
})
export class RxIntuitiveToothEditorBodyComponent extends BaseDestroyableComponent implements OnInit, OnChanges, OnDestroy {
	@Input() teeth: IntuitiveToothForTreatment[] = [];
	@Input() isReadOnly = false;
	@Input() isBridge = false;
	@Input() teethFlatOverride: Tooth[] | null = null;
	@Input() ponticDesigns: IdName[] = [];

	@Output() crownFormChanged = new EventEmitter<CrownForm>();
	@Output() additionalInfoApplied = new EventEmitter<{ toothIds: number[]; result: AdditionalInfoResult }>();
	@Output() clearDisabledFields = new EventEmitter<{ toothIds: number[]; payload: Partial<Record<string, unknown>> }>();
	@Output() ponticDesignChanged = new EventEmitter<{ toothIds: number[]; ponticDesignId: PonticDesignEnum | null }>();

	private readonly teethInput$ = new BehaviorSubject<IntuitiveToothForTreatment[]>([]);
	private crownContext: CrownSelectionContext | null = null;

	protected readonly ponticDesignControl = new FormControl<IdName<PonticDesignEnum> | null>(null);

	readonly hasAdditionalInfoButtonSaved$: Observable<boolean> = combineLatest([this.teethInput$]).pipe(
		map(([teethFromInput]) => {
			if (!this.teethFlatOverride || this.teethFlatOverride.length === 0) {
				return false;
			}

			return this.additionalInfoRules.hasAdditionalInfoButtonSaved(
				this.teethFlatOverride,
				(teethFromInput ?? []).map(t => t.toothId)
			);
		}),
		distinctUntilChanged()
	);

	constructor(
		private readonly indicationsService: RxIntuitiveIndicationRulesService,
		private readonly additionalInfoRules: RxIntuitiveCrownAdditionalInfoRulesService,
		private readonly additionalInfoModalService: RxIntuitiveAdditionalInfoModalService,
		private readonly shellQuery: ShellQuery,
		private readonly logger: LoggerService,
		private readonly teethDiagramBridgeFacade: RxIntuitiveTeethDiagramBridgeFacade
	) {
		super();
	}

	ngOnInit(): void {
		this.ponticDesignControl.valueChanges
			.pipe(
				filter(() => !this.isReadOnly),
				takeUntil(this.componentAlive$)
			)
			.subscribe(value => {
				const toothIds = this.teeth.map(t => t.toothId);
				const ponticDesignId = value?.Id ?? null;

				this.ponticDesignChanged.emit({ toothIds, ponticDesignId });
			});
	}

	override ngOnDestroy(): void {
		this.teethInput$.complete();
		super.ngOnDestroy();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.teeth) {
			this.teethInput$.next(this.teeth ?? []);
			this.ponticDesignControl.setValue(this.sourceOfTruthTooth?.ponticDesign ?? null, { emitEvent: false });
		}
	}

	get sourceOfTruthTooth(): IntuitiveToothForTreatment | undefined {
		// Yes this is just a first tooth.
		// The reason it works is we _know_ whether you can select multiple teeth or have anything set to those.
		return this.teeth[0];
	}

	get indicationId(): IntuitiveIndication {
		return this.sourceOfTruthTooth?.indicationId ?? this.indicationsService.getDefaultIndication();
	}

	get isPonticIndication(): boolean {
		return this.indicationId === IntuitiveIndication.Pontic;
	}

	get crownForm(): CrownForm {
		const teethIds = this.teeth.map(t => t.toothId);

		return {
			teethIds,
			material: this.sourceOfTruthTooth?.material?.Id,
			shadeSystem: this.sourceOfTruthTooth?.shadeSystem?.Id,
			shadeBody: this.sourceOfTruthTooth?.shadeBody,
			specification: this.sourceOfTruthTooth?.specification?.Id
		};
	}

	get customLabMaterialGroup(): string | undefined {
		return this.sourceOfTruthTooth?.customLabMaterialGroup;
	}

	get shouldShowPonticSelector() {
		return this.isPonticIndication && this.ponticDesigns?.length;
	}

	get shouldShowImplantsSummary(): boolean {
		return this.teeth[0]?.indicationId === IntuitiveIndication.BridgeImplantBased;
	}

	get treatmentPreviewLine(): string {
		return buildTreatmentPreviewLine(this.teeth[0]);
	}

	get hasTreatmentPreviewLine(): boolean {
		return this.treatmentPreviewLine !== TREATMENT_PREVIEW_LINE_EMPTY;
	}

	get implantsTreatmentSummary(): string {
		return this.teeth[0]?.implantsTreatmentSummary ?? '';
	}

	onCrownChange(crownForm: CrownForm): void {
		this.crownFormChanged.emit(crownForm);
	}

	onCrownSelectionContext(ctx: CrownSelectionContext): void {
		const prevMaterialOption = this.crownContext?.materialOption;

		this.crownContext = ctx;

		if (!this.isReadOnly && ctx.materialOption && prevMaterialOption !== ctx.materialOption) {
			this.clearDisabledAdditionalInfoFields(ctx.materialOption);
		}
	}

	onAdditionalInfoClick(): void {
		if (!this.crownContext) {
			return;
		}

		if (!this.sourceOfTruthTooth) {
			return;
		}

		const initialValues = extractAdditionalInfoIds(this.sourceOfTruthTooth);
		const shouldValidateForSend = this.shellQuery.shouldValidateForSend;

		this.additionalInfoModalService
			.open(this.isReadOnly, this.crownContext, initialValues, shouldValidateForSend)
			.pipe(take(1))
			.subscribe({
				next: result => {
					if (!result) {
						return;
					}

					const toothIds = this.teeth.map(t => t.toothId);

					this.additionalInfoApplied.emit({ toothIds, result });
				},
				error: err => {
					this.logger.error('Failed to open additional info modal', {
						module: 'RxIntuitiveToothEditorBodyComponent',
						extendedParameters: { error: String(err) }
					});
				}
			});
	}

	onImplantBasedSummaryClick(): void {
		const indicationId = this.teeth[0]?.indicationId;

		if (indicationId === IntuitiveIndication.BridgeImplantBased) {
			const modalResult$ = this.teethDiagramBridgeFacade.openModalForUnitTypeIfNeeded(indicationId);

			if (modalResult$) {
				modalResult$.pipe(takeUntil(this.componentAlive$)).subscribe();
			}
		}
	}

	private clearDisabledAdditionalInfoFields(materialOption: MaterialOption): void {
		const clearPayload = getDisabledFieldsClearPayload(materialOption);

		if (!clearPayload) {
			return;
		}

		const toothIds = this.teeth.map(t => t.toothId);

		this.clearDisabledFields.emit({ toothIds, payload: clearPayload });
	}
}
